import { useQueryClient } from '@tanstack/react-query';
import { Platform } from 'react-native';
import type { AxiosResponse, AxiosError } from 'axios';
import { getGetTasksByCourseModuleQueryKey } from '@/common/api/endpoints/tasks/tasks';
import { useAppStateStore } from '@/common/context/AppStateStore';
import { useTaskSubmissionStore } from '@/student/task/stores/TaskSubmissionStore';
import {
  useUploadAttachment,
  useDeleteAttachment,
} from '@/common/api/endpoints/submission-attachments/submission-attachments';
import { AttachmentUploadResponse } from '@/common/api/models/attachmentUploadResponse';
import { AttachmentDeleteResponse } from '@/common/api/models/attachmentDeleteResponse';
import log from '@/common/utils/logger';
import type { ApiErrorResponse } from '@/common/utils/apiErrorReporter';
import type { Task } from '@/common/api/models/task';
import type { Attachment } from '@/common/api/models/attachment';
import type { GenericApiResponseListTask } from '@/common/api/models/genericApiResponseListTask';

// Helper for optimistic updates - works with raw API response structure
const updateTaskAttachments = (
  taskId: string,
  updater: (attachments: (Attachment & { _isOptimistic?: boolean; isDeleting?: boolean })[]) =>
  (Attachment & { _isOptimistic?: boolean; isDeleting?: boolean })[],
) => (old: AxiosResponse<GenericApiResponseListTask> | undefined) => {
  if (!old?.data?.data) return old;
  return {
    ...old,
    data: {
      ...old.data,
      data: old.data.data.map((t: Task) => (t.id === taskId
        ? {
          ...t,
          submission: {
            ...t?.submission,
            attachments: updater((t?.submission?.attachments || []) as (Attachment & { _isOptimistic?: boolean; isDeleting?: boolean })[]),
          },
        }
        : t)),
    },
  } as AxiosResponse<GenericApiResponseListTask>;
};

export const useAttachmentMutations = () => {
  const queryClient = useQueryClient();
  const selectedModule = useAppStateStore((state) => state.selectedModule);
  const courseId = selectedModule?.courseId;
  const moduleId = selectedModule?.moduleId;

  const queryKey = getGetTasksByCourseModuleQueryKey(courseId || '', moduleId || '', { include: 'submissions,resources' });

  const isExpectedUploadError = (err: AxiosError) => {
    const data = err.response?.data as ApiErrorResponse | undefined;
    const errorCode = data?.error?.code;
    const isFileError = err.response?.status === 400
      && (errorCode === 'SUBMISSION_ATTACHMENT_INVALID_FILETYPE' || errorCode === 'SUBMISSION_ATTACHMENT_MISSING_FILE');
    const isSizeError = err.response?.status === 413 && errorCode === 'SUBMISSION_CONTENT_TOO_LARGE';
    return isFileError || isSizeError;
  };

  const uploadAttachmentMutation = useUploadAttachment({
    mutation: {
      retry: false,
      onMutate: async ({ taskId, data: { file } }) => {
        await queryClient.cancelQueries({ queryKey });
        const previousData = queryClient.getQueryData(queryKey);

        const optimisticAttachment = {
          id: `temp-${Date.now()}`,
          taskId,
          userId: 'temp',
          filename: Platform.OS === 'web' ? (file as File).name : 'attachment',
          filesize: Platform.OS === 'web' ? (file as File).size : 0,
          thumbnailUrl: Platform.OS === 'web' ? URL.createObjectURL(file as Blob) : null,
          _isOptimistic: true,
        };

        queryClient.setQueryData(
          queryKey,
          updateTaskAttachments(taskId, (attachments) => [...attachments, optimisticAttachment]),
        );

        return { previousData, optimisticAttachment };
      },
      onSuccess: (response, { taskId }) => {
        // The response.data.data is now AttachmentUploadResponse (containing attachment + new etag)
        const payload = response.data?.data as AttachmentUploadResponse | undefined;
        const attachment = payload?.data;
        const newEtag = payload?.etag;

        // Sync ETag from attachment upload response
        if (newEtag) {
          useTaskSubmissionStore.getState().setEtag(newEtag);
        }

        if (attachment) {
          queryClient.setQueryData(
            queryKey,
            updateTaskAttachments(taskId, (attachments) => attachments.map((a) => {
              if (a.id.startsWith('temp-')) {
                return {
                  ...attachment,
                  // Use server thumbnailUrl if available, otherwise keep the optimistic one
                  thumbnailUrl: attachment.thumbnailUrl || (Platform.OS === 'web' ? a.thumbnailUrl : null),
                  // Remove the _isOptimistic flag since this is now real server data
                };
              }
              return a;
            })),
          );
        }
      },
      onError: (err: AxiosError, variables, context) => {
        if (context?.previousData) {
          queryClient.setQueryData(queryKey, context.previousData);
        }
        // Only invalidate on error to ensure we're in sync
        queryClient.invalidateQueries({ queryKey });
        if (isExpectedUploadError(err)) {
          log.warn('Attachment upload rejected by validation', err);
          return;
        }
        log.error('Error uploading attachment', err);
      },
      onSettled: (data, error, variables, context) => {
        // Only revoke blob URL if we have a server thumbnail or there was an error
        if (Platform.OS === 'web' && context?.optimisticAttachment?.thumbnailUrl) {
          const serverThumbnail = (data?.data?.data as AttachmentUploadResponse | undefined)?.data?.thumbnailUrl;
          if (error || serverThumbnail) {
            URL.revokeObjectURL(context.optimisticAttachment.thumbnailUrl);
          }
        }

        // Only invalidate if there was an error to trigger a refetch
        if (error) {
          queryClient.invalidateQueries({ queryKey });
        }
      },
    },
  });

  const deleteAttachmentMutation = useDeleteAttachment({
    mutation: {
      retry: false,
      onMutate: async ({ taskId, attachmentId }) => {
        await queryClient.cancelQueries({ queryKey });
        const previousData = queryClient.getQueryData(queryKey);

        // Mark the attachment as being deleted
        queryClient.setQueryData(
          queryKey,
          updateTaskAttachments(taskId, (attachments) => attachments.map((a) => (a.id === attachmentId ? { ...a, isDeleting: true } : a))),
        );

        return { previousData, taskId, attachmentId };
      },
      onSuccess: (response, { taskId, attachmentId }) => {
        // The response.data.data is now AttachmentDeleteResponse (containing attachment + new etag)
        const payload = response.data?.data as AttachmentDeleteResponse | undefined;
        const newEtag = payload?.etag;

        // Sync ETag from attachment deletion response
        if (newEtag) {
          useTaskSubmissionStore.getState().setEtag(newEtag);
        }

        // Remove the attachment from the cache
        queryClient.setQueryData(
          queryKey,
          updateTaskAttachments(taskId, (attachments) => attachments.filter((a) => a.id !== attachmentId)),
        );
      },
      onError: (err, variables, context) => {
        if (context?.previousData) {
          queryClient.setQueryData(queryKey, context.previousData);
        }
        // Only mark as stale without triggering immediate refetch
        queryClient.invalidateQueries({
          queryKey,
          refetchType: 'inactive',
        });
      },
    },
  });

  return {
    uploadAttachmentMutation,
    deleteAttachmentMutation,
  };
};

export default useAttachmentMutations;
